import { ApiError } from "../api/http_client.js";
import { getContentConfig } from "../api/requests/player.requests.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { DEFAULT_MENU_LAYOUT, MenuLayoutConfig, normalizeMenuLayout } from "../ui/screens/menu_screen/menu_side_slots.js";

const CACHE_KEY = "menu_layout_v1";
const CONFIG_TYPE = "menu_layout";

/**
 * Server content-config `menu_layout`: which column each side icon of the
 * menu goes to, its order there and whether it shows at all. A server without
 * the type (404) leaves the bundled default in place.
 */
export class MenuLayoutConfigService
{
  static hasCachedConfig(): boolean
  {
    return PersistentCache.read<unknown>( CACHE_KEY ) !== null;
  }

  private static config: MenuLayoutConfig = MenuLayoutConfigService.readCached() ?? DEFAULT_MENU_LAYOUT;
  private static loading: Promise<MenuLayoutConfig> | null = null;
  private static readonly listeners = new Set<() => void>();

  private static readCached(): MenuLayoutConfig | null
  {
    const raw = PersistentCache.read<unknown>( CACHE_KEY );
    return raw ? normalizeMenuLayout( raw ) : null;
  }

  static get(): MenuLayoutConfig
  {
    return this.config;
  }

  /** Called after the layout changed; returns the unsubscribe function. */
  static onChange( listener: () => void ): () => void
  {
    this.listeners.add( listener );
    return () => this.listeners.delete( listener );
  }

  static apply( config: MenuLayoutConfig ): void
  {
    const changed = JSON.stringify( config ) !== JSON.stringify( this.config );
    this.config = config;
    if ( changed )
      for ( const listener of this.listeners ) listener();
  }

  static async refresh(): Promise<MenuLayoutConfig>
  {
    if ( this.loading ) return this.loading;

    this.loading = ( async () =>
    {
      try
      {
        const server_config = await getContentConfig<unknown>( CONFIG_TYPE );
        this.apply( normalizeMenuLayout( server_config ) );
        PersistentCache.write( CACHE_KEY, this.config );
        console.log( "[MenuLayoutConfig] loaded", this.config );
      }
      catch ( e )
      {
        if ( e instanceof ApiError && e.status === 404 )
        {
          this.apply( DEFAULT_MENU_LAYOUT );
          PersistentCache.remove( CACHE_KEY );
          console.info( "[MenuLayoutConfig] no server config, using bundled layout" );
        }
        else
        {
          console.warn( "[MenuLayoutConfig] failed to fetch config", e );
        }
      }
      finally
      {
        this.loading = null;
      }

      return this.config;
    } )();

    return this.loading;
  }
}
